import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DefaultOptions, useApis, useSetFetchState, useSetLoading } from '../../global-state';
import { ContentAssessmentDto, ContentWithIdDto, UserQuestionDto } from '../../apis';
import { BlockTypes } from '../../enums/blockTypes';
import { PageLoaderLayer } from '../../components/loading-handling/loader-layers/page-loader-layer/page-loader-layer';
import { PagePath } from '../../navigation/navigation.enums';
import { ArticleSkeleton } from './article.skeleton';
import styles from './article.module.css';
import classNames from 'classnames';
import { AssessmentState } from '../../enums/assessmentState';
import { ActionCard, Anchor, ArrowIcon, Button, FocusPanel, FormControl, PageHeader, RequiredValidator, SearchIcon, SkillPercentage, Textarea } from '@keplerco/core';
import { PreAssessmentIcon } from '../../components/pre-assessment.icon';
import { QuestionnaireWidget } from '../../components/questionnaire/questionnaire';
import { FetchStatus, FetchType } from '../../enums/fetch';
import { useForm } from 'react-hook-form';
interface IFormValues {
  prompt: string;
}
export function ArticlePage(): JSX.Element {
  const { topicId } = useParams();
  const { topicApi, assessmentApi } = useApis();
  const [takeQuiz, setTakeQuiz] = useState<boolean>(false);
  const [assessment, setAssessment] = useState<ContentAssessmentDto>();
  const [content, setContent] = useState<ContentWithIdDto>();
  const [prompt, setPrompt] = useState<string>('');

  const { handleSubmit, control, setValue } = useForm<any>({ mode: 'onChange' });
  const [learnMore, setLearnMore] = useState<boolean>(false);
  const setLoading = useSetLoading();
  const setFetchState = useSetFetchState();

  async function onSubmitHandler(values: IFormValues) {
    setLearnMore(false);
    await getPageData(values.prompt);
  }

  async function getPageData(prompt: string | undefined) {
    if (!topicId) return;
    setFetchState(FetchStatus.Active, FetchType.PageFetching);
    topicApi.topicContentGet(topicId, prompt, DefaultOptions)
      .then(x => {
        setContent(x);
        setFetchState(FetchStatus.Inactive, FetchType.PageFetching);
      });
  }

  async function getNext(next: boolean) {
    if (!topicId) return;
    setFetchState(FetchStatus.Active, FetchType.PageFetching);
    topicApi.topicNextGet(topicId, content?.id!, next, DefaultOptions)
      .then(x => {
        setContent(x);
        setFetchState(FetchStatus.Inactive, FetchType.PageFetching);
      });
  }

  useEffect(() => {
    getPageData('');
  }, []);

  return (
    <div className="pageWrapper wrapper">
      <React.Fragment>

        <PageLoaderLayer path={PagePath.article} skeletonLoader={<ArticleSkeleton />}>


          {content != undefined &&
            <PageHeader
              breadcrumbs={[
                {
                  name: 'Skills',
                  url: PagePath.skills
                },
                {
                  name: content!.skillName!, url: PagePath.skill.replace(":skillId", content!.skillId!)
                },
                {
                  name: content!.heading!, url: window.location.pathname
                }]}
            />}

          {content?.assessmentStatus !== AssessmentState.Assessed && (
            <ActionCard
              icon={() => <PreAssessmentIcon />}
              actionText="Take the quiz"
              description={`Let's test how well you have learned this article and level up`}
              onClick={async () => {
                setLoading(true);
                setAssessment(
                  await assessmentApi.assessmentContentIdGet(content!.id!, DefaultOptions)
                );
                setLoading(false);
                setTakeQuiz(true);
              }}
              title="Once you're ready, take the test"
            />
          )}

          {content?.assessmentStatus === AssessmentState.Assessed && !content?.hasNext && (
            <ActionCard
              icon={() => <SkillPercentage notAssessed={false} percentage={content.score!} />}
              actionText="Learn more"
              description={content.score! < 70 ?
                'That was tough. What was difficult and what else would you like to learn about this topic?' :
                'Welldone, you got it! What else would you like to learn about this topic?'}
              onClick={async () => {
                setLearnMore(true);
              }}
              title="Quiz Complete"
            />
          )}

          {content?.assessmentStatus === AssessmentState.Assessed && !!content?.hasNext && (
            <ActionCard
              icon={() => <SkillPercentage notAssessed={false} percentage={content.score!} />}
              actionText="Read Next"
              description={content.score! < 70 ?
                'That was tough. Try again with the next article' :
                'Welldone, you got it! Learn more with the next article'}
              onClick={async () => {
                await getNext(true);
              }}
              title="Quiz Complete"
            />
          )}

          {(content?.hasNext || content?.hasPrevious) &&
            <div className={styles.row}>
              <span>
                {(content?.hasPrevious) &&
                  <Button
                    tiny

                    type="button"
                    theme="dark"
                    square
                    onClick={async () => {
                      await getNext(false);
                    }}>
                    Read Rrevious
                  </Button>

                }
              </span>
              <span>
                {(content?.hasNext) &&
                  <Button
                    tiny

                    type="button"
                    theme="dark"
                    square
                    onClick={async () => {
                      await getNext(true);
                    }}
                  >
                    Read Next
                  </Button>
                }
              </span>
            </div>
          }


          {content?.blocks != undefined &&
            content?.blocks?.length > 0 &&
            content?.blocks?.map(block => {
              switch (block.type) {
                case BlockTypes.CodeContent:
                  return <pre>
                    <code className={classNames([styles.code])}>
                      {block.value}
                    </code>
                  </pre>
                case BlockTypes.Heading:
                  return <h1 className={classNames('heading1', [styles.heading, styles.copy])}>
                    {block.value}
                  </h1>
                case BlockTypes.ListItem:
                  return <ul>
                    <li className={classNames([styles.copy])}>
                      {block.value}
                    </li>
                  </ul>
                case BlockTypes.MiniHeadline:
                  return <h4 className={classNames('heading4', [styles.heading, styles.copy])}>
                    {block.value}
                  </h4>
                case BlockTypes.Paragraph:
                  return <p className={classNames('body2', [styles.copy])}> {block.value}</p>
                case BlockTypes.SubHeading:
                  return <h3 className={classNames('heading3', [styles.heading, styles.copy])}>
                    {block.value}
                  </h3>
                case BlockTypes.ThematicBreak:
                  return <hr />
                case BlockTypes.Unknown:
                  return <p className={classNames('body2', [styles.copy])}> {block.value}</p>
              }
            })
          }

        </PageLoaderLayer>
        {/* variation */}

        {content?.blocks != undefined &&
          content?.blocks?.length > 0 &&
          <FocusPanel zIndex={103} open={takeQuiz} variation="questionnaire" onClose={() => setTakeQuiz(false)}>
            {!!assessment?.questions && (
              <QuestionnaireWidget
                sections={[{
                  name: content?.heading!,
                  id: content?.id!,
                  assessment: assessment,
                  isComplete: assessment.assessmentState == AssessmentState.Assessed,
                  totalQuestions: assessment.questions!.length,
                  totalAnsweredQuestions: assessment.questions!.filter(x => x.answers!.some(item => item.answered)).length,
                }]}
                variation="questionnaire"
                onCompleteAll={async () => {
                  setTakeQuiz(false);
                  await getPageData('');
                }}
              />
            )}
          </FocusPanel>
        }

      </React.Fragment>
      {/* variation */}
      <FocusPanel zIndex={103}
        open={learnMore} variation="standard" onClose={() => setLearnMore(false)}>
        <form id="search-roles" onSubmit={handleSubmit(onSubmitHandler)}>
          <div className="dialogBodyLayout">
            <div className="card" style={{ position: 'relative', marginBottom: 30 }}>
              <div className="row">
                <div className="column">
                  <FormControl
                    control={control}
                    rules={new RequiredValidator('Enter a prompt')}
                    name="prompt"
                    render={({ field, fieldState }) => {
                      return <Textarea {...field} haserror={!!fieldState.error} label="What else would you like to learn on this topic?" responsive />;
                    }}
                  />
                </div>
              </div>
              <Button
                fullWidthMobile
                type="button"
                square
                theme="dark"
              >
                <div style={{ display: 'flex', gap: 15, alignItems: 'center', justifyContent: 'center' }}>
                  Search
                  <SearchIcon />
                </div>
              </Button>
            </div>
          </div>
        </form>
      </FocusPanel>
    </div >);
}

